//import './assets/main.css'
//console.log(window.location.protocol + '//api.' + window.location.host)
import { createApp } from 'vue'
import App from './App.vue'
import { createRouter } from './router'
import { createStore } from './store/store'
import Toasted from '@hoppscotch/vue-toasted'
import * as Sentry from '@sentry/vue'
import { websocket } from './websocket'
import SmoothReflow from './components/SmoothReflow.vue'
import { installChunkReloader, isChunkLoadError } from './lib/chunkReloader'
import { sentryIgnoreErrors } from './lib/sentryIgnoreErrors'
import { createVerificationGate } from './lib/memberVerificationGate'
import { axios } from './request'

import '@hoppscotch/vue-toasted/style.css'
import infiniteScroll from './lib/infiniteScroll'
import { getTerms, phrasePlugin } from './terminology'

//You should set VITE_API_URL IN PRODUCTION
let url = import.meta.env.VITE_API_URL
if (url === '') {
  url = window.location.protocol + '//api.' + window.location.host
}

const app = createApp(App)
app.config.globalProperties.$apiURL = url
app.use(phrasePlugin)

app.use(infiniteScroll)

app.use(Toasted, { className: 'toastedNotification' })
app.component('SmoothReflow', SmoothReflow)

getTerms().then(() => {
  const router = createRouter(app)
  const store = createStore(app)

  installChunkReloader({ router })

  // #501 — a member with an open verification is held on /os-meus-dados.
  const verificationGate = createVerificationGate({
    role: () => store.state.auth?.jwt?.CustomUserInfo?.Role,
    memberId: () => store.state.auth?.jwt?.CustomUserInfo?.MemberID,
    fetchMine: () => axios.get('member_verifications/mine').then((res) => res.data?.data ?? null),
  })
  router.beforeEach(verificationGate.guard)
  app.config.globalProperties.$verificationGate = verificationGate

  // The DSN lives in front/.env.production<org> (VITE_SENTRY_DSN) rather than in
  // source. An org whose env file lacks it reports nothing, so setting it is part
  // of the onboarding checklist (docs/onboarding-a-new-coop.md).
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  if (import.meta.env.VITE_MODE === 'production' && sentryDsn) {
    Sentry.init({
      app,
      dsn: sentryDsn,
      // Every org reports to the same Sentry project, so the `org` tag is the
      // only thing on the event that says which one produced it (#336).
      initialScope: { tags: { org: import.meta.env.VITE_CLIENT } },
      // Events are proxied through the API (api/Modules/sentry/controller.go),
      // so the IP and geolocation Sentry shows are the API SERVER's, not the
      // user's — a "GB, Slough" event is the datacenter, not a user in Slough.
      // The tunnel posts to a project id hardcoded there: keep the DSN above on
      // that same project or events go nowhere.
      tunnel: url + '/sentry',
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          // Additional SDK configuration goes in here, for example:
          maskAllText: false,
          blockAllMedia: true,
        }),
      ],
      // Drop errors thrown by scripts the browser injects into our pages —
      // they are not ours to fix and cannot be reproduced (#440). See the file
      // for why the list must stay narrow.
      ignoreErrors: sentryIgnoreErrors,
      // Drop old-deploy chunk-load errors: they are already auto-recovered by
      // installChunkReloader (router.onError + vite:preloadError), so reporting
      // them is pure post-recovery noise that buries genuine router errors.
      beforeSend(event, hint) {
        if (isChunkLoadError(hint?.originalException)) return null
        return event
      },
      // Performance Monitoring
      tracesSampleRate: 0.2, // Capture 100% of the transactions, reduce in production!
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
  }

  app.use(router)
  app.use(store)

  window.vue = app.mount('#app')

  websocket.init()
})
